import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BreakpointObserver} from '@angular/cdk/layout';
import {filter} from 'rxjs';
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatButtonModule} from "@angular/material/button";
import {CommonModule} from "@angular/common";
import {SessionService} from "./state/session.service";
import {MatDialog} from "@angular/material/dialog";
import {WebviewDialogComponent} from "./webview/webview-dialog.component";
import {SearchbarDialogComponent} from "./searchbar/searchbar-dialog.component";
import {environment} from "../environments/environment";
import {MatIconModule} from "@angular/material/icon";
import {AgentAccessDialogComponent} from "./agent/agent-access-dialog.component";

interface NavigationItem {
  label: string;
  route?: string;
  action?: 'agents' | 'webview' | 'searchbar';
  sportsOnly?: boolean;
}

interface NavigationGroup {
  id: string;
  label: string;
  icon: string;
  items: NavigationItem[];
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatSidenavModule, MatButtonModule, CommonModule, RouterLink, RouterLinkActive, MatIconModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  isMenuCollapsed = false;
  isCompact = false;
  customerSupportOnly = 'CUSTOMER_SUPPORT_ONLY' in environment
    && environment.CUSTOMER_SUPPORT_ONLY === true;
  readonly expandedGroups = new Set<string>();
  readonly routeMatchOptions: IsActiveMatchOptions = {
    paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored'
  };
  readonly primaryLinks = [
    {label: 'Conversations', route: '/conversations', icon: 'forum'},
    {label: 'Messages', route: '/conversation-overview', icon: 'chat_bubble_outline'},
    {label: 'Dashboard', route: '/dashboard', icon: 'bar_chart'},
  ];
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly groups: NavigationGroup[] = [
    {id: 'content', label: 'Content', icon: 'menu_book', items: [
      {label: 'Knowledge', route: '/knowledge-overview'},
      {label: 'Assets', route: '/knowledge-assets'},
      {label: 'Products', route: '/product-overview', sportsOnly: true},
    ]},
    {id: 'ai', label: 'AI setup', icon: 'tune', items: [
      {label: 'System config', route: '/system-prompt'},
      {label: 'Evaluation rules', route: '/evaluation-catalog'},
      {label: 'Models', route: '/ai-model'},
      {label: 'Data labels', route: '/data-labels'},
      {label: 'Welcome suggestions', route: '/welcome-suggestions', sportsOnly: true},
    ]},
    {id: 'sports', label: 'Sports', icon: 'emoji_events', items: [
      {label: 'Leagues', route: '/league-list', sportsOnly: true},
      {label: 'Clubs', route: '/club-list', sportsOnly: true},
      {label: 'Players', route: '/player-list', sportsOnly: true},
      {label: 'Matches', route: '/match-list', sportsOnly: true},
    ]},
    {id: 'admin', label: 'Admin', icon: 'settings', items: [
      {label: 'Clients', route: '/client-overview'},
      {label: 'Users', route: '/user-list'},
      {label: 'Export API', action: 'agents'},
    ]},
    {id: 'preview', label: 'Preview', icon: 'web', items: [
      {label: 'Webview', action: 'webview', sportsOnly: true},
      {label: 'Searchbar', action: 'searchbar', sportsOnly: true},
    ]},
  ];

  constructor(private router: Router,
              private dialog: MatDialog,
              public session: SessionService) {
  }

  get navigationGroups(): NavigationGroup[] {
    return this.groups.map(group => ({
      ...group,
      items: group.items.filter(item => !item.sportsOnly || !this.customerSupportOnly)
    })).filter(group => group.items.length > 0);
  }

  trackGroup(_index: number, group: NavigationGroup): string {
    return group.id;
  }

  trackItem(_index: number, item: NavigationItem): string {
    return item.label;
  }

  toggleGroup(id: string, event: Event): void {
    event.preventDefault();
    if (this.expandedGroups.has(id)) {
      this.expandedGroups.delete(id);
    } else {
      this.expandedGroups.add(id);
    }
  }

  runAction(action: NavigationItem['action']): void {
    if (this.isCompact) this.isMenuCollapsed = true;
    switch (action) {
      case 'agents': this.showAgents(); break;
      case 'webview': this.showWebview(); break;
      case 'searchbar': this.showSearchbar(); break;
    }
  }

  private expandActiveGroup(): void {
    for (const group of this.navigationGroups) {
      if (group.items.some(item => item.route && this.router.isActive(item.route, this.routeMatchOptions))) {
        this.expandedGroups.add(group.id);
      }
    }
  }

  showAgents() {
    this.dialog.open(AgentAccessDialogComponent, {
      width: '760px',
      maxWidth: '94vw'
    });
  }

  ngOnInit() {
    this.expandActiveGroup();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.expandActiveGroup();
      if (this.isCompact) this.isMenuCollapsed = true;
    });
    this.breakpoints.observe('(max-width: 900px)').pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.isCompact = state.matches;
        this.isMenuCollapsed = state.matches;
      });
    // TODO: add localhost:4200/assets/promptbet.js/css so this also works in local mode
    document.body.appendChild(
      Object.assign(document.createElement('script'), {
        src: environment.CDN_URL + '/promptbet.js',
        id: 'promptbet-client-script'
      })
    );

    document.body.appendChild(
      Object.assign(document.createElement('link'), {
        href: environment.CDN_URL + '/promptbet.css',
        rel: 'stylesheet',
        id: 'promptbet-client-style'
      })
    );
  }

  logout() {
    this.isMenuCollapsed = false;
    this.session.setAuthToken(undefined);
    this.router.navigate(['/login']);
  }

  showWebview() {
    this.dialog.open(WebviewDialogComponent, {width: '90%', maxWidth: '600px', height: '90%', maxHeight: '1000px'});
  }

  showSearchbar() {
    this.dialog.open(SearchbarDialogComponent, {width: '90%', maxWidth: '600px', height: '90%', maxHeight: '1000px'});
  }

  toggleMenuCollapsed() {
    this.isMenuCollapsed = !this.isMenuCollapsed;
  }
}
